import React, { ChangeEvent, FC, FocusEvent } from "react";
import {
  Controller,
  Control,
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
} from "react-hook-form";
import { useSelector } from "react-redux";
import classNames from "classnames";

import { TextArea, Icon, Button, Input, Switch } from "../../common";
import { useInvoiceCurrencies } from "../../../hooks/useInvoiceCurrencies";
import { selectInvoice } from "../../../store/reducers/invoice";
import { CurrencyType, Invoice } from "../../../types/InvoiceTypes";
import { isNumber } from "../../../utils/numbers";
import { useValidateInvoiceAmountThreshold } from "../../../hooks/useValidateInvoiceAmountThreshold";

type Props = {
  control: Control<Invoice>;
  decimals?: number;
  fields: FieldArrayWithId<Invoice, "items", "id">[];
  append: UseFieldArrayAppend<Invoice, "items">;
  remove: UseFieldArrayRemove;
};

export const InvoiceTable: FC<Props> = ({
  control,
  fields,
  append,
  remove,
}) => {
  const invoice = useSelector(selectInvoice);
  const invoiceCurrencies = useInvoiceCurrencies(invoice);

  const { msg: amountThresholdMsg } =
    useValidateInvoiceAmountThreshold(invoice);

  if (!invoice) {
    return <></>;
  }

  const {
    incoming: incomingCurrency,
    input: inputCurrency,
    outgoing: outgoingCurrency,
  } = invoiceCurrencies!;

  const showExactPayoutToggle =
    incomingCurrency?.type === CurrencyType.FIAT &&
    outgoingCurrency?.type === CurrencyType.CRYPTO;

  const currency = inputCurrency?.alternativeSymbol || inputCurrency?.symbol;

  return (
    <>
      <div className="flex flex-row justify-between items-center py-4">
        <span className="theme-caption uppercase">Job</span>

        {showExactPayoutToggle && (
          <Controller
            name="items_amounts_are_in_outgoing_currency"
            control={control}
            render={({ field: { value, ...rest } }) => (
              <Switch
                id="items_amounts_are_in_outgoing_currency"
                checked={value}
                {...rest}
              >
                Fill in job amounts in {outgoingCurrency.code}
                <div className="theme-text-secondary text-sm">
                  Final invoice will be in {incomingCurrency.code}
                </div>
              </Switch>
            )}
          />
        )}
      </div>

      <ul>
        {fields.map((item, index) => (
          <li key={item.id} className="relative flex flex-row w-full group">
            <Controller
              name={`items.${index}.description`}
              control={control}
              render={({ field }) => (
                <TextArea
                  className={classNames(
                    "group-first:rounded-tl-lg group-last:rounded-bl-lg rounded-none border-r-0 hover:border-gray-100 active:border-gray-100 focus-visible:border-gray-100 h-[82px]",
                    {
                      "border-t-0": index !== 0,
                    }
                  )}
                  wrapperClassName="flex-1 flex"
                  placeholder="Describe a job done with units, hours or tax details if needed"
                  maxLength={250}
                  rows={2}
                  bindToContext
                  {...field}
                />
              )}
            />

            <Controller
              name={`items.${index}.rate`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Input
                  type="text"
                  className="hover:border-gray-100 border-none active:border-gray-100 focus-visible:border-gray-100"
                  wrapperClassName={classNames(
                    "basis-[280px] h-[82px] border border-gray-100 group-first:rounded-tr-lg group-last:rounded-br-lg rounded-none",
                    {
                      "border-t-0": index !== 0,
                    }
                  )}
                  {...field}
                  min="0"
                  step="1"
                  postfix={currency}
                  postfixClassName="pr-6"
                  bindToContext
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (!isNumber(e.target.value)) {
                      e.preventDefault();
                      return;
                    }

                    field.onChange(e);
                  }}
                  onBlur={(e: FocusEvent<HTMLInputElement>) => {
                    e.target.value = Number(e.target.value).toFixed(2);
                    field.onChange(e);
                  }}
                />
              )}
            />

            {index !== 0 && (
              <div className="absolute -right-16 top-3">
                <Button
                  variant={Button.variant.SECONDARY}
                  onClick={() => remove(index)}
                  icon
                >
                  <Icon icon="Minus" size={24} disableFill />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      <div className="w-full flex flex-row justify-end mt-4">
        <Button
          variant={Button.variant.SECONDARY}
          onClick={() => append({ description: "", rate: 0 })}
        >
          <Icon icon="Add" size={24} disableFill /> One more row
        </Button>
      </div>

      {amountThresholdMsg}
    </>
  );
};
